import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  CircularProgress,
  Grid,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { PRODUCT_IDS } from "../../utils/entitlements";

import {
  deleteInvitation,
  getInvitationResponses,
  getInvitationStats,
  getInvitationTemplates,
  getUserInvitations,
} from "../../utils/invitationsApi";
import "./Invitations.css";

const InvitationsDashboard = () => {
  const { hasProduct, entitlementsLoading } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copyMessage, setCopyMessage] = useState(null);
  const [stats, setStats] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [rsvpError, setRsvpError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const activeInvitation = useMemo(
    () => (invitations.length > 0 ? invitations[0] : null),
    [invitations]
  );

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [templateData, invitationData] = await Promise.all([
          getInvitationTemplates(),
          getUserInvitations(),
        ]);

        setTemplates(templateData);
        setInvitations(invitationData);
      } catch (err) {
        console.error(err);
        setError(err.message || "Настана грешка при вчитување на податоците.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  useEffect(() => {
    const fetchStatsAndRsvps = async () => {
      if (!activeInvitation?.id) {
        setStats(null);
        setRsvps([]);
        setRsvpLoading(false);
        return;
      }

      try {
        const [statsResponse, rsvpResponse] = await Promise.all([
          getInvitationStats(activeInvitation.id),
          getInvitationResponses(activeInvitation.id),
        ]);
        setStats(statsResponse);
        setRsvps(Array.isArray(rsvpResponse) ? rsvpResponse : []);
        setRsvpError(null);
      } catch (err) {
        console.error(err);
        setStats(null);
        setRsvps([]);
        setRsvpError(err.message || "Неуспешно вчитување на RSVP одговори.");
      } finally {
        setRsvpLoading(false);
      }
    };

    setRsvpLoading(true);
    fetchStatsAndRsvps();
  }, [activeInvitation?.id]);

  const hasInvitation = Boolean(activeInvitation);
  const hasInvitationsProduct = hasProduct(PRODUCT_IDS.INVITATIONS);
  const canCreateInvitation = !hasInvitation && hasInvitationsProduct;

  const handleCopyLink = async (slug) => {
    try {
      const shareUrl = `${window.location.origin}/invite/${slug}`;
      await navigator.clipboard.writeText(shareUrl);
      setCopyMessage("Линкот е копиран во clipboard.");
      setTimeout(() => setCopyMessage(null), 3000);
    } catch (err) {
      console.error("Clipboard error:", err);
      setCopyMessage("Неуспешно копирање на линк.");
      setTimeout(() => setCopyMessage(null), 3000);
    }
  };

  const handleDelete = async (invitationId) => {
    const confirmed = window.confirm(
      "Сигурни сте дека сакате да ја избришете поканата?"
    );
    if (!confirmed) {
      return;
    }

    try {
      setDeleteLoading(true);
      await deleteInvitation(invitationId);
      setInvitations((prev) => prev.filter((item) => item.id !== invitationId));
      setStats(null);
      setRsvps([]);
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно бришење на поканата.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderResponseLabel = (value) => {
    switch ((value || "").toLowerCase()) {
      case "yes":
        return "Да";
      case "no":
        return "Не";
      case "maybe":
        return "Можеби";
      default:
        return value || "-";
    }
  };

  if (loading) {
    return (
      <Box className="invitation-page-container">
        <CircularProgress />
        <Typography mt={2}>Вчитување...</Typography>
      </Box>
    );
  }

  return (
    <Box className="invitation-page-container">
      <Stack spacing={2} alignItems="center" className="invitation-header">
        <Tabs
          value={0}
          onChange={(_, value) => {
            if (value === 1) {
              navigate("/save-the-dates");
            }
          }}
          aria-label="invitation tabs"
          TabIndicatorProps={{ style: { backgroundColor: "var(--primary-color)" } }}
          sx={{
            ".MuiTabs-flexContainer": { gap: 2 },
            ".MuiTab-root": {
              textTransform: "none",
              color: "#7a7a7a",
              fontWeight: 500,
              "&.Mui-selected": {
                color: "var(--primary-color)",
                fontWeight: 700,
              },
            },
          }}
        >
          <Tab label="Покани" />
          <Tab label="Save the Date" />
        </Tabs>
        <Typography variant="h4" component="h1" textAlign="center">
          Дигитални покани
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Одберете шаблон, прилагодете ја содржината и споделете го вашиот
          уникатен линк со гостите.
        </Typography>
        <Button
          component={RouterLink}
          to="/invitations/new"
          variant="contained"
          disabled={!canCreateInvitation}
          sx={{
            backgroundColor: "var(--primary-color)",
            "&:hover": { backgroundColor: "var(--primary-color-dark)" },
            opacity: canCreateInvitation ? 1 : 0.6,
          }}
          title={
            hasInvitation
              ? "Може да уредите постоечката покана."
              : !hasInvitationsProduct
              ? "За креирање покана потребен е активен пакет."
              : undefined
          }
        >
          Нова покана
        </Button>
        {!entitlementsLoading && !hasInvitationsProduct && (
          <Alert severity="info">
            За креирање покана активирајте го пакетот за покани од{" "}
            <RouterLink to="/pricing">ценовник</RouterLink>.
          </Alert>
        )}
        {copyMessage && <Alert severity="info">{copyMessage}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>

      <Box className="invitation-section">
        <Typography variant="h5" component="h2" gutterBottom>
          Ваши покани
        </Typography>
        {hasInvitation && stats ? (
          <Card className="invitation-card stats-card">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                RSVP статистика
              </Typography>
              <Grid container spacing={2} className="invitation-stats-grid">
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    Вкупно
                  </Typography>
                  <Typography variant="h6">{stats.total_responses}</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    Потврдени
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {stats.yes_count}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    Одбиени
                  </Typography>
                  <Typography variant="h6" color="error.main">
                    {stats.no_count}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    Можеби
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    {stats.maybe_count}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ) : null}

        {invitations.length === 0 ? (
          <Typography color="text.secondary">
            Сè уште немате креирано покани.
          </Typography>
        ) : (
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {invitations.map((invitation) => {
              const shareUrl = `${window.location.origin}/invite/${invitation.slug}`;
              return (
                <Grid item xs={12} md={6} lg={4} key={invitation.id}>
                  <Card className="invitation-card">
                    <CardContent>
                      <Typography variant="h6">{invitation.title}</Typography>
                      {invitation.event_date && (
                        <Typography variant="body2" color="text.secondary">
                          {new Date(invitation.event_date).toLocaleString()}
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        className="invitation-share-link"
                      >
                        {shareUrl}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        onClick={() => handleCopyLink(invitation.slug)}
                        sx={{
                          color: "var(--primary-color)",
                          "&:hover": {
                            backgroundColor: "rgba(227, 95, 135, 0.08)",
                          },
                        }}
                      >
                        Копирај линк
                      </Button>
                      <Button
                        size="small"
                        component={RouterLink}
                        to={`/invite/${invitation.slug}`}
                        target="_blank"
                        sx={{
                          color: "var(--primary-color)",
                          "&:hover": {
                            backgroundColor: "rgba(227, 95, 135, 0.08)",
                          },
                        }}
                      >
                        Преглед
                      </Button>
                      <Button
                        size="small"
                        component={RouterLink}
                        to={`/invitations/${invitation.id}/edit`}
                        sx={{
                          color: "var(--primary-color)",
                          "&:hover": {
                            backgroundColor: "rgba(227, 95, 135, 0.08)",
                          },
                        }}
                      >
                        Уреди
                      </Button>
                      <Button
                        size="small"
                        onClick={() => handleDelete(invitation.id)}
                        disabled={deleteLoading}
                        startIcon={<DeleteIcon fontSize="small" />}
                        sx={{
                          color: "var(--primary-color)",
                          "&:hover": {
                            backgroundColor: "rgba(227, 95, 135, 0.08)",
                          },
                        }}
                      >
                        Избриши
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      <Box className="invitation-section">
        <Typography variant="h5" component="h2" gutterBottom>
          Шаблони
        </Typography>

        {templates.length === 0 ? (
          <Typography color="text.secondary">
            Нема достапни шаблони во моментов.
          </Typography>
        ) : (
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {templates.map((template) => (
              <Grid item xs={12} sm={6} md={4} key={template.id}>
                <Card className="invitation-card">
                  {template.thumbnail_url && (
                    <CardMedia
                      component="img"
                      height="160"
                      image={template.thumbnail_url}
                      alt={template.name}
                    />
                  )}
                  <CardContent>
                    <Typography variant="h6">{template.name}</Typography>
                    {template.premium ? (
                      <Typography
                        variant="body2"
                        color="secondary"
                        fontWeight="bold"
                      >
                        Премиум
                        {template.price ? ` - ${Number(template.price)} ден.` : ""}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Бесплатен шаблон
                      </Typography>
                    )}
                  </CardContent>
                  {!hasInvitation && (
                    <CardActions>
                      <Button
                        size="small"
                        component={RouterLink}
                        to={`/invitations/new?templateId=${template.id}`}
                        disabled={!hasInvitationsProduct}
                        sx={{
                          color: "var(--primary-color)",
                          "&:hover": {
                            backgroundColor: "rgba(227, 95, 135, 0.08)",
                          },
                        }}
                        title={
                          hasInvitationsProduct
                            ? undefined
                            : "Потребен е активен пакет за покани."
                        }
                      >
                        Користи шаблон
                      </Button>
                    </CardActions>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {hasInvitation && (
        <Box className="invitation-section">
          <Typography variant="h5" component="h2" gutterBottom>
            RSVP одговори
          </Typography>
          {rsvpLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Вчитување...
              </Typography>
            </Stack>
          ) : rsvpError ? (
            <Alert severity="error">{rsvpError}</Alert>
          ) : rsvps.length === 0 ? (
            <Typography color="text.secondary">
              Нема заведени RSVP одговори за оваа покана.
            </Typography>
          ) : (
            <div className="invitation-rsvp-table">
              <div className="invitation-rsvp-scroll">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className="name-col">Име</TableCell>
                      <TableCell className="response-col">Одговор</TableCell>
                      <TableCell className="guests-col">Вкупно гости</TableCell>
                      {/* <TableCell className="message-col">Порака</TableCell> */}
                      <TableCell className="date-col">Испратено</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rsvps.map((entry) => {
                      const attendeeCount =
                        entry?.number_of_people === undefined || entry?.number_of_people === null
                          ? "-"
                          : entry.number_of_people;
                      const hasDate = Boolean(entry.created_at);
                      let desktopTime = "-";
                      let mobileDate = "-";
                      let tooltipDate = "-";
                      if (hasDate) {
                        const created = new Date(entry.created_at);
                        if (!Number.isNaN(created.getTime())) {
                          desktopTime = created.toLocaleTimeString(undefined, {
                            hour: "2-digit",
                            minute: "2-digit",
                          });
                          mobileDate = created.toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          });
                          tooltipDate = created.toLocaleString();
                        }
                      }

                      return (
                        <TableRow key={entry.id}>
                          <TableCell className="name-col">{entry.name || "-"}</TableCell>
                          <TableCell className="response-col">{renderResponseLabel(entry.response)}</TableCell>
                          <TableCell className="guests-col">{attendeeCount}</TableCell>
                          {/* <TableCell className="message-col" title={entry.message || "-"}>
                            {entry.message || "-"}
                          </TableCell> */}
                          <TableCell className="date-col" title={tooltipDate}>
                            {hasDate ? (
                              <>
                                <span className="date-desktop">{desktopTime}</span>
                                <span className="date-mobile">{mobileDate}</span>
                              </>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </Box>
      )}
    </Box>
  );
};

export default InvitationsDashboard;
